import { useTranslation } from 'react-i18next';
import { useSelectedOrganization } from 'app/state/contexts/UserContext';
import { Link } from '@tanstack/react-router';
import { useRequiredParams } from 'app/router/params';
import { useRef } from 'react';

import FileZone, { FileZoneTemplate } from 'shared/forms/FileZone';
import moment from 'moment';
import { DetailsTable } from './details-table';
import { useXlsxFile, useReferenceData, referenceDataKeys, useXlsxFiles } from 'entities/reference-data';

import { useQueryClient } from '@tanstack/react-query';
import { calculatePageOffset } from 'slate/components/page/utils';
import { UpdateFinancialData, CurrentFileBlob, type XlsxFile } from '../update-modal/UpdateReferenceData';
import { FilesListView } from './FilesListView';
import { Clickable } from 'shared/ui/Clickable';
import { showTaggerWarning, tableHasTags } from '../update-modal/updatePermissions';
import { usePermissions } from 'app/api/permission-queries';
import Tippy from '@tippyjs/react';
import { useEditorDocumentQuery } from 'entities/document';
import { useCreateAccessLog } from 'entities/access-log';

interface ModalHandle {
  open: () => void;
  close: () => void;
}

interface UsageEntry {
  pageNumber: number;
  sectionSuperId: string;
  pageId?: string;
}

interface ReferenceDataDetailsProps {
  showDetails: boolean;
  setShowDetails: (show: boolean) => void;
  viewLocale?: string;
}

export default function ReferenceDataDetails(props: ReferenceDataDetailsProps) {
  const { t } = useTranslation('reference-data');

  const { showDetails, setShowDetails, viewLocale } = props;
  const { documentId, refId } = useRequiredParams('documentId', 'refId');
  const queryClient = useQueryClient();
  const xlsxFiles = useXlsxFiles(documentId);

  const modal = useRef<ModalHandle | null>(null);

  const referenceData = useReferenceData(refId, documentId);
  const { documentPermissions } = usePermissions(documentId);
  const taggerWarning = showTaggerWarning(tableHasTags(referenceData.data?.selectionContent), documentPermissions);
  const xlsxFile = useXlsxFile(referenceData.data?.fileRevisionId);

  const handleClickForValueUpdate = () => {
    modal.current?.open();
  };

  const handleUploadSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [...referenceDataKeys.xlsxFilesAll],
    });
    setShowDetails(true);
  };

  const handleUpdateAbort = () => {
    modal.current?.close();
  };

  const handleUpdateSuccess = () => {
    modal.current?.close();
  };

  return (
    <div className="card-body">
      {referenceData.isLoading || !referenceData.data ? null : (
        <div>
          <div className="d-flex mb-2 align-items-center">
            <h3 className="mt-1 mb-0 two-line-overflow">{referenceData.data.name}</h3>
            <div className="ms-2 text-nowrap">
              <UsedTableIcon documentId={documentId} usage={referenceData.data.usage} />
            </div>
          </div>
          <p className="date-text">{moment(referenceData.data.createdAt).format('D MMM. YYYY, HH:mm')}</p>
          {referenceData.isLoading || !referenceData.data.selectionContent ? (
            <div className="row justify-content-center">
              <div className="col-4">
                <FileZone type="xlsx" documentId={documentId} onSuccess={handleUploadSuccess} url={'/gaby/reference-data/xlsx'} />
              </div>
              {!xlsxFiles.data?.length ? null : (
                <>
                  <div className="col-1 d-flex align-items-center justify-content-center">{t('details.links.or')}</div>
                  <div className="col-4" onClick={() => modal.current?.open()}>
                    <FileZoneTemplate title={t('details.link.add-to-uploaded-document')} icon="fa-regular fa-link" />
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {taggerWarning && <p className="compare-permission-notice">{t('review-tags.no-tag-permission')}</p>}
              {false ? null : (
                <CurrentFileBlob file={xlsxFile.data?.selectedXlsx as XlsxFile | undefined}>
                  <Clickable
                    className="update-selection-values"
                    onClick={() => {
                      handleClickForValueUpdate();
                    }}
                  >
                    {t('details.link.update-selection-values')}
                  </Clickable>
                </CurrentFileBlob>
              )}
              <DetailsTable viewLocale={viewLocale} />
            </>
          )}
        </div>
      )}
      {showDetails && <FilesListView onCloseClick={() => setShowDetails(false)} referenceData={referenceData} onUploadSuccess={handleUploadSuccess} />}
      <UpdateFinancialData modalRef={modal} onAbort={handleUpdateAbort} onSuccess={handleUpdateSuccess} />
    </div>
  );
}

interface UsedTableIconProps {
  usage?: UsageEntry[];
  documentId: string;
}

const UsedTableIcon = ({ usage, documentId }: UsedTableIconProps) => {
  const { t } = useTranslation('reference-data');

  const { data: document } = useEditorDocumentQuery({ documentId });
  const offset = calculatePageOffset(document?.content?.pageNumbering);
  const { sectionPermissions } = usePermissions(documentId);
  const { mutate: createAccessLog } = useCreateAccessLog();
  const organizationId = useSelectedOrganization();

  const renderIcon = (metadata: UsageEntry, message: string) => {
    const hasSectionPermission = metadata?.sectionSuperId in sectionPermissions;
    return (
      <Tippy content={<p className="gaby-tooltip arrow-bottom">{t('details.tag.no-access')}</p>} disabled={hasSectionPermission}>
        <Link
          to="/documents/$documentId/sections/$sectionId"
          params={{ documentId: String(documentId), sectionId: String(metadata.sectionSuperId) }}
          hash={metadata.pageId}
          key={metadata.pageNumber}
          className={`in-doc-message ${!hasSectionPermission && 'disabled'}`}
          onClick={() =>
            hasSectionPermission ? createAccessLog({ sectionSuperId: metadata.sectionSuperId, documentSuperId: documentId, organizationId }) : null
          }
        >
          {message}
        </Link>
      </Tippy>
    );
  };

  const pageNumber = (usage: UsageEntry) => usage.pageNumber + offset;
  const sortedUsage = usage?.slice().sort((a: UsageEntry, b: UsageEntry) => pageNumber(a) - pageNumber(b));

  return sortedUsage?.length ? (
    sortedUsage.map((u: UsageEntry) => renderIcon(u, t('details.tag.used-in-document') + ' ' + (pageNumber(u) < 1 ? '-' : pageNumber(u))))
  ) : (
    <span className="in-doc-message">{t('details.tag.missing-in-document')}</span>
  );
};
